import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as UploadIcon } from "../../../assets/icons/upload.svg";
import { Button } from "../../../components/Button";
import { CourseSelection } from "./CourseSelection";
import { Dialog } from "../../../components/Dialog";
import { fileUpload } from "../../../helpers/fileUpload";
import { api } from "../../../helpers/api";
import { CourseForm } from "../../content/ContentDetails/components/CourseForm";
import { ContentDetails } from "../../content/ContentDetails/ContentDetails";
import { PlaylistUpload } from "./PlaylistUpload";

type PlaylistFormProps = {
  onClose: () => void;
};

type PublisherType = {
  publisher_id: string;
  full_name: string;
  user_name: string;
  email_id: string;
  user_image: string | null;
  mobile: string;
};

type StepType = "details" | "courses" | "create" | "content_details";

export function PlaylistForm({ onClose }: PlaylistFormProps) {
  const [step, setStep] = useState<StepType>("details");
  const [formData, setFormData] = useState<any>({
    title: "",
    description: "",
    selectedCourses: [],
    image: null,
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [publisher, setPublisher] = useState<null | PublisherType>(null);
  const [isCourseFormOpen, setIsCourseFormOpen] = useState(false);
  const [createdCourse, setCreatedCourse] = useState<any>(null);
  const [isContentDetailsOpen, setIsContentDetailsOpen] = useState(false);

  useEffect(() => {
    const storedPublisher = localStorage.getItem("selectedPublisher");
    if (storedPublisher) {
      setPublisher(JSON.parse(storedPublisher));
    }
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (loading) return;
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (selectedCourses: string[]) => {
    setLoading(true);
    const formDataToSubmit = {
      playlist_title: formData.title,
      playlist_description: formData.description,
      playlist_image: "",
      publisher_id: publisher ? publisher?.publisher_id : "",
      video_ids: selectedCourses,
    };

    if (formData.image) {
      const { response, url } = await fileUpload(
        formData.image,
        "COURSE_COVER"
      );
      formDataToSubmit.playlist_image = url;
    }

    try {
      await api.createPlaylist(formDataToSubmit).then(() => {
        onClose();
      });
    } catch (error) {
      console.error("Error creating playlist:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case "details":
        return (
          <div className="flex flex-col gap-4 p-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-black font-medium tracking-[-0.24px]">
                <div className="opacity-80">
                  <span>Cover </span>
                  <span className="!text-[#444444]">
                    (Recommended size 400x500)
                  </span>
                </div>
              </label>
              <button
                onClick={() => {
                  if (loading) return;
                  if (imageInputRef.current) {
                    imageInputRef.current.click();
                  }
                }}
                className={`cursor-pointer w-[84px] h-[105px] bg-[#e4ebe4] rounded-[7px] border border-dashed border-[#355e3b] flex-col justify-center items-center flex ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="cover"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UploadIcon className="w-6 h-6" />
                )}
              </button>
              <input
                type="file"
                className="hidden"
                ref={imageInputRef}
                accept="image/*"
                onChange={handleImageChange}
                disabled={loading}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Playlist Title</label>
              <input
                type="text"
                className={`border rounded-md p-2 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                disabled={loading}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Description</label>
              <textarea
                className={`border rounded-md p-2 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                rows={4}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                disabled={loading}
              />
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => setStep("courses")}
                disabled={!formData.title || loading}
              >
                {loading ? "Saving..." : "Next"}
              </Button>
            </div>
          </div>
        );

      default:
        return (
          <PlaylistUpload
            initialSelectedCourses={formData.selectedCourses}
            loading={loading}
            onBack={() => setStep("details")}
            onSubmit={(selectedCourses) => {
              if (loading) return;
              const videoIds = selectedCourses.map(
                (course) => course?.video_id
              );
              setFormData({ ...formData, selectedCourses: videoIds });
              handleSubmit(videoIds);
            }}
          />
        );
    }
  };

  return (
    <Dialog
      isOpen={true}
      onClose={loading ? () => {} : onClose}
      className={`${
        step === "create" || step === "content_details"
          ? "w-screen h-screen max-w-none"
          : "w-[80vw] max-w-[1400px]"
      }`}
      title={
        step === "details"
          ? "Create New Playlist"
          : step === "courses"
          ? "Select Courses"
          : step === "create"
          ? "Create Course"
          : "Course Details"
      }
    >
      {renderStep()}
    </Dialog>
  );
}
